import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const USAGE_MD = `# Form Handler — Usage Guide

## What is this?

A serverless form-collection platform running on Cloudflare. Centralise form submissions from multiple websites, auto-send email notifications, store to a database, and export to CSV.

**Deployed at:**
- API: \`https://forms.conversionhive.au\`
- Admin dashboard: \`https://dash.conversionhive.au\` (Google login)
- AI integration (MCP): \`https://forms.conversionhive.au/mcp\` — see **MCP (AI integration)** in the sidebar

---

## Day-to-day workflow

### 1. Sign in

Go to \`https://dash.conversionhive.au\` → click **Sign in with Google** → log in with \`david@conversionhive.com.au\`.

> Only emails in the \`ALLOWED_EMAILS\` secret can sign in. To add another admin: \`npx wrangler secret put ALLOWED_EMAILS\` (value: comma-separated email list).

### 2. Create a client (= one form / one customer website)

- Left sidebar → **Clients** → **+ New client**
- Fill in:
  - **Client name**: internal label, e.g. "Nu Style Shutters contact form"
  - **Notify emails**: recipients (one email per line — the system will email each submission to all of them)
  - **Allowed origins**: websites allowed to submit (one full URL per line, **must match exactly including protocol**, e.g. \`https://nustyleshutters.com.au\`)
  - **Webhook URL** (optional): each submission will also be POSTed here
  - **Turnstile enabled**: whether to turn on Cloudflare bot protection (on by default; you can turn it off on new sites while testing, then re-enable)

### 3. Design the form fields

In the **Form schema** section:
- Click **+ Add field** to add a field
- For each field:
  - **Field name**: snake_case, e.g. \`full_name\`, \`phone_number\` (this is the DB key)
  - **Label**: what the user sees
  - **Type**: \`text\` / \`email\` / \`tel\` / \`textarea\` / \`select\` / \`checkbox\` / \`number\` / \`url\`
  - **Required**: whether the field must be filled
  - **Placeholder / Help text**: supplementary hints
  - **Max length / Min / Max**: length or numeric range limits
- Drag ⠿ to reorder fields
- Below the fields, **Form settings** lets you customise the submit button text, success message, and email subject / body template

After saving you will get a **Client ID** (format \`cli_xxxxxxxx\`) — this is what you paste into your website.

### 4. View submissions

- **Clients** → click a client name → **View submissions**
- Filter by status (email failed, webhook failed, pending)
- Click a row to see full detail
- Top right **Export CSV** downloads everything

### 5. Audit Log

Left sidebar → **Audit Log** — every admin action (who changed what and when), useful for tracking changes.

---

## Embedding the form on a website (4 methods)

Assume your Client ID is \`cli_abc12345\`.

### Method 1 — Direct link (simplest)

Don't modify the website at all — just link to the hosted form from a "Contact us" button:

\`\`\`
https://forms.conversionhive.au/f/cli_abc12345
\`\`\`

This opens a standalone form page that shows a success message after submit.

---

### Method 2 — iframe embed

Embed the form inside an existing web page:

\`\`\`html
<iframe
  src="https://forms.conversionhive.au/f/cli_abc12345"
  width="100%"
  height="600"
  style="border:0"
  title="Contact form">
</iframe>
\`\`\`

Pros: fully isolated, won't collide with your site's CSS. Cons: can't restyle.

---

### Method 3 — Embed script (auto-render, most flexible)

Drop this onto the page:

\`\`\`html
<div data-ch-form="cli_abc12345"></div>
<script src="https://forms.conversionhive.au/embed.js" async></script>
\`\`\`

The script replaces the \`<div>\` with a form using basic styles, which you can override via your own CSS.

**Important:** your website's origin (e.g. \`https://nustyleshutters.com.au\`) must be listed in **Allowed origins** in the dashboard, or submissions will be blocked by CORS.

---

### Method 4 — Custom HTML + direct API

Write your own HTML and CSS and just POST to the submit endpoint:

\`\`\`html
<form id="contact-form">
  <input name="name" required>
  <input type="email" name="email" required>
  <textarea name="message" required></textarea>
  <!-- Honeypot field to catch bots -->
  <input type="hidden" name="_hp_website" style="display:none" tabindex="-1" autocomplete="off">
  <button type="submit">Send</button>
</form>

<script>
document.getElementById('contact-form').addEventListener('submit', async (e) => {
  e.preventDefault();
  const data = Object.fromEntries(new FormData(e.target));
  const res = await fetch('https://forms.conversionhive.au/submit/cli_abc12345', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (json.ok) alert('Thanks!');
  else alert(json.error);
});
</script>
\`\`\`

**Important:** your website origin must still be added to **Allowed origins** in the dashboard.

The form field \`name\` attributes must match the **Field name** values you set in the dashboard exactly.

---

### File uploads

A form field of type **File upload** lets visitors attach photos (JPG, PNG, WebP, HEIC) or PDFs — up to 10 MB each, 5 files and 20 MB per submission. The form must keep bot protection (reCAPTCHA or Turnstile). Methods 1–3 handle uploads by themselves. With method 4, send the form as multipart and put the bot token in a header:

\`\`\`html
<input type="file" name="photos" accept="image/jpeg,image/png,image/webp,application/pdf" multiple>

<script>
// inside your submit handler, after getting the reCAPTCHA / Turnstile token:
const res = await fetch('https://forms.conversionhive.au/submit/form_abc123', {
  method: 'POST',
  headers: { 'X-Captcha-Token': token },   // no Content-Type — the browser sets it
  body: new FormData(form),
});
</script>
\`\`\`

Notification emails, the submission page and webhooks show each file as a download link. Links in emails and webhooks work for as long as the submission is kept (90 days) and stop working when it is deleted.

## About Turnstile (bot protection)

If Turnstile is enabled for the client, methods 2, 3, and 4 need the widget added to the form:

\`\`\`html
<!-- Inside the <form> -->
<div class="cf-turnstile" data-sitekey="YOUR_SITE_KEY"></div>
<script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>
\`\`\`

You can get the site key from each client's detail page (the Raw HTML snippet and embed script both include it automatically; you only need to add it manually for method 4).

---

## FAQ

**Q: I submitted but didn't receive an email.**
- Check **Submissions** → look at the \`email_status\` column (\`sent\` / \`failed\` / \`pending\`).
- If \`failed\` → open that submission and read \`email_last_error\`.
- If your SMTP2GO API key expired → reset it: \`npx wrangler secret put SMTP2GO_API_KEY\`.

**Q: My website shows a CORS error on submit.**
- Go to the client settings → add the website URL to **Allowed origins** (include \`https://\`, no trailing slash).

**Q: How do I add a new admin login?**
\`\`\`sh
cd worker && npx wrangler secret put ALLOWED_EMAILS
# Enter: david@conversionhive.com.au,other@example.com
\`\`\`

**Q: How long are submissions kept?**
90 days, after which they are auto-purged (a cron job runs daily at 03:00 UTC). Manual deletes are soft — they're permanently purged 7 days later.

**Q: Does the webhook retry on failure?**
Yes. 4 fast initial retries plus up to 8 more via a cron job every 15 minutes (≈2 hours total).

**Q: What's the payload size limit?**
100 KB. File uploads are not supported.
`;

export default function DocsUsage() {
  return (
    <div className="docs-page">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{USAGE_MD}</ReactMarkdown>
    </div>
  );
}
